/*
 * Reachability check of the backend, used by the global error modal to tell "the server
 * answered with an error" apart from "the server cannot be reached" (down, restarting,
 * network lost, gateway errors from the reverse proxy).
 * Never throws: resolves to true / false.
 */

const PROBE_TIMEOUT_MS = 4000;
// Answered by a reverse proxy / load balancer when the backend behind it is down
const GATEWAY_STATUSES = new Set([502, 503, 504]);

async function probe(mode) {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS) : null;
  try {
    return await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/`, {
      mode,
      cache: "no-store",
      credentials: "omit",
      signal: controller?.signal,
    });
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function isBackendReachable() {
  try {
    // Any answer from the backend itself (even 404) means it is up
    const response = await probe("cors");
    return !GATEWAY_STATUSES.has(response.status);
  } catch {
    // CORS may be missing on "/": an opaque no-cors answer still proves the server is up
    try {
      await probe("no-cors");
      return true;
    } catch {
      return false;
    }
  }
}

/* Delay before the next reconnection attempt: 3 s, 5 s, 10 s, then every 15 s */
export function reconnectDelayMs(attempt) {
  return [3000, 5000, 10000][attempt] ?? 15000;
}
